<script setup>
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import PillsCell from '@js/components/tables/cells/PillsCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                    width: 0,
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'publishedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Publication',
                    width: 0,
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.publishedAt,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'isPublished',
            enableSorting: false,
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Publiée',
                    width: 0,
                }),
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isPublished,
                    align: 'center',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'title',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Titre',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.title,
                    length: 80,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'newsTheme',
            enableSorting: false,
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Thème',
                    width: 0,
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.newsTheme.label,
                    customBackgroundColor: props.row.original.newsTheme.color,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'tenantTypes',
            enableSorting: false,
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Destinataires',
                }),
            cell: (props) =>
                props.row.original.tenantTypes.length
                    ? h(PillsCell, {
                          pillValues: props.row.original.tenantTypes.map(
                              (tenantType) => ({
                                  value: tenantType.label,
                                  type: 'secondary',
                              }),
                          ),
                      })
                    : h(PillCell, {
                          pillValue: 'Tous',
                          type: 'primary',
                      }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.apikom.newsArticle',
                                    {
                                        newsArticle: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.apikom.postNewsArticles'),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="route('front.projectManagement.apikom.newsArticle')"
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
